import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import {
  CreditCard,
  Plus,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Star,
  Users,
  MessageSquare,
  Smartphone,
  Key,
  Webhook,
  DollarSign,
  Clock
} from 'lucide-react'
import { adminApi } from '@/lib/api'

interface Plan {
  _id: string
  name: string
  description: string
  price: number
  currency: string
  recurringInterval: 'day' | 'week' | 'month' | 'year'
  features: string[]
  dailyLimit: number
  monthlyLimit: number
  bulkSendLimit: number
  maxDevices: number
  maxApiKeys: number
  maxWebhooks: number
  isActive: boolean
  isPopular: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
  stats?: {
    activeSubscriptions: number
    totalSubscriptions: number
    revenue: number
  }
}

interface PlanFormData {
  name: string
  description: string
  price: string
  currency: string
  recurringInterval: 'day' | 'week' | 'month' | 'year'
  features: string[]
  dailyLimit: string
  monthlyLimit: string
  bulkSendLimit: string
  maxDevices: string
  maxApiKeys: string
  maxWebhooks: string
  isActive: boolean
  isPopular: boolean
  sortOrder: string
}

export default function PlanManagement() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [planToDelete, setPlanToDelete] = useState<Plan | null>(null)
  const [currentFeature, setCurrentFeature] = useState('')
  
  const [formData, setFormData] = useState<PlanFormData>({
    name: '',
    description: '',
    price: '0',
    currency: 'USD',
    recurringInterval: 'month',
    features: [],
    dailyLimit: '100',
    monthlyLimit: '3000',
    bulkSendLimit: '50',
    maxDevices: '3',
    maxApiKeys: '5',
    maxWebhooks: '3',
    isActive: true,
    isPopular: false,
    sortOrder: '0'
  })

  const queryClient = useQueryClient()

  const { data: plansData, isLoading } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => adminApi.get('/admin/plans').then((res) => res.data.data?.plans || []),
    refetchInterval: 30000,
  })

  const createPlanMutation = useMutation({
    mutationFn: (planData: any) => adminApi.post('/admin/plans', planData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
      setCreateDialogOpen(false)
      resetForm()
    }
  })

  const updatePlanMutation = useMutation({
    mutationFn: ({ planId, planData }: { planId: string; planData: any }) => 
      adminApi.put(`/admin/plans/${planId}`, planData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
      setEditDialogOpen(false)
      resetForm()
      setSelectedPlan(null)
    }
  })

  const deletePlanMutation = useMutation({
    mutationFn: (planId: string) => adminApi.delete(`/admin/plans/${planId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
      setDeleteDialogOpen(false)
      setPlanToDelete(null)
    }
  })

  const togglePlanStatusMutation = useMutation({
    mutationFn: ({ planId, isActive }: { planId: string; isActive: boolean }) => 
      adminApi.patch(`/admin/plans/${planId}/status`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
    }
  })

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '0',
      currency: 'USD',
      recurringInterval: 'month',
      features: [],
      dailyLimit: '100',
      monthlyLimit: '3000',
      bulkSendLimit: '50',
      maxDevices: '3',
      maxApiKeys: '5',
      maxWebhooks: '3',
      isActive: true,
      isPopular: false,
      sortOrder: '0'
    })
    setCurrentFeature('')
  }

  const handleCreatePlan = () => {
    const planData = {
      ...formData,
      price: Number(formData.price),
      dailyLimit: Number(formData.dailyLimit),
      monthlyLimit: Number(formData.monthlyLimit),
      bulkSendLimit: Number(formData.bulkSendLimit),
      maxDevices: Number(formData.maxDevices),
      maxApiKeys: Number(formData.maxApiKeys),
      maxWebhooks: Number(formData.maxWebhooks),
      sortOrder: Number(formData.sortOrder)
    }
    createPlanMutation.mutate(planData)
  }

  const handleEditPlan = (plan: Plan) => {
    setSelectedPlan(plan)
    setFormData({
      name: plan.name,
      description: plan.description,
      price: plan.price.toString(),
      currency: plan.currency,
      recurringInterval: plan.recurringInterval,
      features: [...plan.features],
      dailyLimit: plan.dailyLimit.toString(),
      monthlyLimit: plan.monthlyLimit.toString(),
      bulkSendLimit: plan.bulkSendLimit.toString(),
      maxDevices: plan.maxDevices.toString(),
      maxApiKeys: plan.maxApiKeys.toString(),
      maxWebhooks: plan.maxWebhooks.toString(),
      isActive: plan.isActive,
      isPopular: plan.isPopular,
      sortOrder: plan.sortOrder.toString()
    })
    setEditDialogOpen(true)
  }

  const handleUpdatePlan = () => {
    if (!selectedPlan) return
    
    const planData = {
      ...formData,
      price: Number(formData.price),
      dailyLimit: Number(formData.dailyLimit),
      monthlyLimit: Number(formData.monthlyLimit),
      bulkSendLimit: Number(formData.bulkSendLimit),
      maxDevices: Number(formData.maxDevices),
      maxApiKeys: Number(formData.maxApiKeys),
      maxWebhooks: Number(formData.maxWebhooks),
      sortOrder: Number(formData.sortOrder)
    }
    updatePlanMutation.mutate({
      planId: selectedPlan._id,
      planData
    })
  }

  const handleDeletePlan = (plan: Plan) => {
    setPlanToDelete(plan)
    setDeleteDialogOpen(true)
  }

  const confirmDeletePlan = () => {
    if (planToDelete) {
      deletePlanMutation.mutate(planToDelete._id)
    }
  }

  const handleTogglePlanStatus = (plan: Plan) => {
    togglePlanStatusMutation.mutate({
      planId: plan._id,
      isActive: !plan.isActive
    })
  }

  const addFeature = () => {
    if (currentFeature.trim() && !formData.features.includes(currentFeature.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, currentFeature.trim()]
      }))
      setCurrentFeature('')
    }
  }

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }))
  }

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(price)
  }

  const getIntervalText = (interval: string) => {
    const intervals = {
      day: 'Daily',
      week: 'Weekly', 
      month: 'Monthly',
      year: 'Yearly'
    }
    return intervals[interval as keyof typeof intervals] || interval
  }

  const plans = plansData || []

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plan Management</h1>
          <p className="text-gray-600 mt-2">
            Create and manage subscription plans with custom limits and features
          </p>
        </div>
        <Button
          onClick={() => setCreateDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Plan
        </Button>
      </div>

      {/* Plans Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="h-5 w-5 mr-2" />
            Plans ({plans.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Limits</TableHead>
                  <TableHead>Devices</TableHead>
                  <TableHead>Subscriptions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan: Plan) => (
                  <TableRow key={plan._id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        {plan.isPopular && (
                          <Star className="h-4 w-4 text-yellow-500 fill-current" />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{plan.name}</div>
                          <div className="text-sm text-gray-500">{plan.description}</div>
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {formatPrice(plan.price, plan.currency)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {getIntervalText(plan.recurringInterval)}
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm space-y-1">
                        <div className="flex items-center">
                          <MessageSquare className="h-3 w-3 mr-1 text-blue-600" />
                          <span>{plan.monthlyLimit === -1 ? 'Unlimited' : plan.monthlyLimit.toLocaleString()}/mo</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-3 w-3 mr-1 text-green-600" />
                          <span>{plan.dailyLimit === -1 ? 'Unlimited' : plan.dailyLimit}/day</span>
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm space-y-1">
                        <div className="flex items-center">
                          <Smartphone className="h-3 w-3 mr-1 text-purple-600" />
                          <span>{plan.maxDevices}</span>
                        </div>
                        <div className="flex items-center">
                          <Key className="h-3 w-3 mr-1 text-gray-600" />
                          <span>{plan.maxApiKeys} API keys</span>
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium">{plan.stats?.activeSubscriptions || 0} active</div>
                        <div className="text-gray-500">{plan.stats?.totalSubscriptions || 0} total</div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <Badge variant={plan.isActive ? 'default' : 'secondary'}>
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditPlan(plan)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        
                        <Button
                          size="sm"
                          variant={plan.isActive ? "secondary" : "default"}
                          onClick={() => handleTogglePlanStatus(plan)}
                          disabled={togglePlanStatusMutation.isPending}
                        >
                          {plan.isActive ? <PowerOff className="h-3 w-3" /> : <Power className="h-3 w-3" />}
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeletePlan(plan)}
                          disabled={deletePlanMutation.isPending}
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Plan Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Plan</DialogTitle>
            <DialogDescription>
              Create a new subscription plan with custom limits and features
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Plan Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Professional"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the plan"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="price">Price</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="recurringInterval">Billing Interval</Label>
                <Select value={formData.recurringInterval} onValueChange={(value: any) => setFormData(prev => ({ ...prev, recurringInterval: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Daily</SelectItem>
                    <SelectItem value="week">Weekly</SelectItem>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="year">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Limits & Features */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="dailyLimit">Daily SMS Limit</Label>
                  <Input
                    id="dailyLimit"
                    type="number"
                    min="0"
                    value={formData.dailyLimit}
                    onChange={(e) => setFormData(prev => ({ ...prev, dailyLimit: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="monthlyLimit">Monthly SMS Limit</Label>
                  <Input
                    id="monthlyLimit"
                    type="number"
                    min="0"
                    value={formData.monthlyLimit}
                    onChange={(e) => setFormData(prev => ({ ...prev, monthlyLimit: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="bulkSendLimit">Bulk Send Limit</Label>
                  <Input
                    id="bulkSendLimit"
                    type="number"
                    min="0"
                    value={formData.bulkSendLimit}
                    onChange={(e) => setFormData(prev => ({ ...prev, bulkSendLimit: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="maxDevices">Max Devices</Label>
                  <Input
                    id="maxDevices"
                    type="number"
                    min="1"
                    value={formData.maxDevices}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxDevices: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="maxApiKeys">Max API Keys</Label>
                  <Input
                    id="maxApiKeys"
                    type="number"
                    min="1"
                    value={formData.maxApiKeys}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxApiKeys: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="maxWebhooks">Max Webhooks</Label>
                  <Input
                    id="maxWebhooks"
                    type="number"
                    min="1"
                    value={formData.maxWebhooks}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxWebhooks: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="sortOrder">Sort Order</Label>
                <Input
                  id="sortOrder"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4">
            <div>
              <Label>Features</Label>
              <div className="flex gap-2">
                <Input
                  value={currentFeature}
                  onChange={(e) => setCurrentFeature(e.target.value)}
                  placeholder="Add a feature..."
                  onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                />
                <Button type="button" onClick={addFeature}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.features.map((feature, index) => (
                  <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => removeFeature(index)}>
                    {feature} ×
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex items-center space-x-2">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  id="isPopular"
                  type="checkbox"
                  checked={formData.isPopular}
                  onChange={(e) => setFormData(prev => ({ ...prev, isPopular: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="isPopular">Popular</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePlan}
              disabled={createPlanMutation.isPending || !formData.name || !formData.description}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {createPlanMutation.isPending ? 'Creating...' : 'Create Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Plan Dialog - Same structure as Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Plan</DialogTitle>
            <DialogDescription>
              Update the plan settings and limits
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Same form structure as create dialog */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Plan Name</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Professional"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Input
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the plan"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="edit-price">Price</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-currency">Currency</Label>
                  <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit-recurringInterval">Billing Interval</Label>
                <Select value={formData.recurringInterval} onValueChange={(value: any) => setFormData(prev => ({ ...prev, recurringInterval: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Daily</SelectItem>
                    <SelectItem value="week">Weekly</SelectItem>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="year">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="edit-dailyLimit">Daily SMS Limit</Label>
                  <Input
                    id="edit-dailyLimit"
                    type="number"
                    min="0"
                    value={formData.dailyLimit}
                    onChange={(e) => setFormData(prev => ({ ...prev, dailyLimit: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-monthlyLimit">Monthly SMS Limit</Label>
                  <Input
                    id="edit-monthlyLimit"
                    type="number"
                    min="0"
                    value={formData.monthlyLimit}
                    onChange={(e) => setFormData(prev => ({ ...prev, monthlyLimit: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="edit-bulkSendLimit">Bulk Send Limit</Label>
                  <Input
                    id="edit-bulkSendLimit"
                    type="number"
                    min="0"
                    value={formData.bulkSendLimit}
                    onChange={(e) => setFormData(prev => ({ ...prev, bulkSendLimit: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-maxDevices">Max Devices</Label>
                  <Input
                    id="edit-maxDevices"
                    type="number"
                    min="1"
                    value={formData.maxDevices}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxDevices: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="edit-maxApiKeys">Max API Keys</Label>
                  <Input
                    id="edit-maxApiKeys"
                    type="number"
                    min="1"
                    value={formData.maxApiKeys}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxApiKeys: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-maxWebhooks">Max Webhooks</Label>
                  <Input
                    id="edit-maxWebhooks"
                    type="number"
                    min="1"
                    value={formData.maxWebhooks}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxWebhooks: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit-sortOrder">Sort Order</Label>
                <Input
                  id="edit-sortOrder"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Features</Label>
              <div className="flex gap-2">
                <Input
                  value={currentFeature}
                  onChange={(e) => setCurrentFeature(e.target.value)}
                  placeholder="Add a feature..."
                  onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                />
                <Button type="button" onClick={addFeature}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.features.map((feature, index) => (
                  <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => removeFeature(index)}>
                    {feature} ×
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex items-center space-x-2">
                <input
                  id="edit-isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="edit-isActive">Active</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  id="edit-isPopular"
                  type="checkbox"
                  checked={formData.isPopular}
                  onChange={(e) => setFormData(prev => ({ ...prev, isPopular: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="edit-isPopular">Popular</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdatePlan}
              disabled={updatePlanMutation.isPending || !formData.name || !formData.description}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {updatePlanMutation.isPending ? 'Updating...' : 'Update Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{planToDelete?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-red-600 font-medium flex items-center gap-2">
              <span>⚠️ This action cannot be undone and will:</span>
            </div>
            <ul className="text-sm text-gray-600 space-y-2 ml-4">
              <li>• Permanently delete the plan</li>
              <li>• Prevent new subscriptions to this plan</li>
              <li>• Maintain existing active subscriptions</li>
            </ul>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeletePlan}
              disabled={deletePlanMutation.isPending}
            >
              {deletePlanMutation.isPending ? 'Deleting...' : 'Delete Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
